/**
 * Time-bounded local override of server-side values.
 *
 * Backstory: a few of our writes (profile name, follow/unfollow,
 * track likes) succeed at the gateway but the next read returns the
 * pre-write payload because Firefly's `api-cache` and downstream
 * caches haven't expired yet. We can't fix the server, but we can
 * remember locally what the user just changed and prefer that
 * version for a short window.
 *
 * `LocalOverlay` is the generic primitive that drives those
 * service-level helpers — it's not application-aware. Plug it in
 * with `apply` to merge an overlay into a freshly-fetched server
 * payload, or `read` to query the override directly.
 *
 * Each entry has an explicit expiry, so even if a service forgets
 * to `clear()` after a successful write the override eventually
 * heals itself.
 *
 * Optional persistence: pass a {@link PersistentItem} that stores a
 * `Record<string, {value, expiresAt}>` and the overlay survives page
 * refreshes. Hydration is async (the underlying persistence is) but
 * `read` returns synchronously — call `whenHydrated()` if you need to
 * wait. Note: not every value type is persistable. Object URLs and
 * other in-memory handles need to be converted to a serializable form
 * (e.g. a data URL) before being written.
 */

import { createLogPrefix, logDebug, logWarn, type PersistentItem } from '@amazon/vinyl-util'

const target = { logPrefix: createLogPrefix('LocalOverlay') }

const DEFAULT_TTL_MS = 15_000

interface OverlayEntry<V> {
    readonly value: V
    readonly expiresAt: number
    /**
     * Optional cleanup that fires when the entry is dropped — either by
     * an explicit clear/clearAll, by being replaced via a fresh write,
     * or by being read after its TTL elapsed. Lets callers attach
     * resource cleanup (e.g. URL.revokeObjectURL) to a remembered value.
     */
    readonly onEvict?: (value: V) => void
}

export interface OverlayWriteOptions<V> {
    readonly ttlMs?: number
    readonly onEvict?: (value: V) => void
}

export interface LocalOverlay<K, V> {
    /**
     * Returns the override for the key if non-expired, else undefined.
     */
    read(key: K): V | undefined

    /**
     * Sets the override with a TTL (default 15s).
     */
    write(key: K, value: V, options?: OverlayWriteOptions<V>): void

    /**
     * Clears the override for a key.
     */
    clear(key: K): void

    /**
     * Drops every entry.
     */
    clearAll(): void

    /**
     * Resolves once initial hydration from persistence completes (or
     * immediately if no persistence is configured).
     */
    whenHydrated(): Promise<void>

    /**
     * Snapshot of every non-expired entry — for re-applying persisted
     * overrides after hydration (e.g. re-arming a normalized-cache overlay on
     * reload). Drops entries whose TTL has lapsed in passing, like `read`.
     */
    liveEntries(): readonly OverlayEntrySnapshot<K, V>[]
}

export interface OverlayEntrySnapshot<K, V> {
    readonly key: K
    readonly value: V
    /** Absolute epoch-ms deadline, in the injected clock's units. */
    readonly expiresAt: number
}

/**
 * Persisted snapshot shape — `Record<string, {value, expiresAt}>`.
 *
 * Keys are written via `String(key)` and restored as strings on
 * hydration. If you want persistence to round-trip, use `K = string`
 * (or use {@link createSingleValueOverlay}, which manages this for
 * you). Mixing non-string K with persistence loses the lookup on
 * reload because `Map.has(123) !== Map.has("123")`.
 */
type PersistedSnapshot<V> = Record<string, { value: V; expiresAt: number }>

/**
 * Predicate that determines whether a hydrated value matches the
 * caller's current schema. Returning false drops the entry instead of
 * loading it — use this to discard data written by an obsolete
 * version of the code.
 */
export type OverlayValueValidator<V> = (value: unknown) => value is V

class LocalOverlayImpl<K, V> implements LocalOverlay<K, V> {
    private readonly entries = new Map<K, OverlayEntry<V>>()
    private readonly persistence: PersistentItem<PersistedSnapshot<V>> | null
    private readonly hydratedPromise: Promise<void>
    private readonly validate: OverlayValueValidator<V> | null

    // Caller supplies a clock so tests can advance time deterministically.
    constructor(
        private readonly now: () => number,
        persistence: PersistentItem<PersistedSnapshot<V>> | null,
        validate: OverlayValueValidator<V> | null
    ) {
        this.persistence = persistence
        this.validate = validate
        this.hydratedPromise = persistence ? this.hydrate(persistence) : Promise.resolve()
    }

    private async hydrate(p: PersistentItem<PersistedSnapshot<V>>): Promise<void> {
        try {
            const snap = await p.get()
            const now = this.now()
            let dropped = 0
            // The persisted snapshot is JSON — its declared shape is a
            // type assertion at the storage layer, not a runtime
            // guarantee. Walk through `unknown` so we can validate
            // each entry before trusting it.
            for (const [key, raw] of Object.entries(snap as Record<string, unknown>)) {
                if (raw == null || typeof raw !== 'object') {
                    dropped++
                    continue
                }
                const entry = raw as { value: unknown; expiresAt: unknown }
                if (typeof entry.expiresAt !== 'number' || entry.expiresAt <= now) {
                    dropped++
                    continue
                }
                // Validate the value against the caller's schema if one
                // is provided — drops entries written by an obsolete
                // version instead of resurrecting bad data.
                if (this.validate && !this.validate(entry.value)) {
                    dropped++
                    continue
                }
                // Don't clobber entries written between construction and
                // hydration — fresher state always wins.
                if (this.entries.has(key as K)) continue
                this.entries.set(key as K, {
                    value: entry.value as V,
                    expiresAt: entry.expiresAt,
                })
            }
            if (dropped > 0) {
                // If the snapshot contained junk, rewrite it without the
                // bad entries so we don't re-validate on every load.
                logDebug(target, 'hydrate dropped entries', { dropped })
                this.persist()
            }
        } catch (e) {
            // Persistence failures shouldn't cripple the in-memory overlay.
            logWarn(target, 'hydrate failed', e)
        }
    }

    private persist(): void {
        if (!this.persistence) return
        // Snapshot synchronously; write asynchronously. We don't await —
        // overlay reads are sync and don't need to know about durability.
        const snap: PersistedSnapshot<V> = {}
        const now = this.now()
        for (const [key, entry] of this.entries) {
            if (entry.expiresAt <= now) continue
            // onEvict is intentionally dropped from the snapshot — it's a
            // function and not serializable. The caller's persistence
            // setup should reattach cleanup hooks during hydration if it
            // needs them.
            snap[String(key)] = { value: entry.value, expiresAt: entry.expiresAt }
        }
        this.persistence.set(snap).catch(() => {
            // Persistence failures don't propagate — the overlay remains
            // valid in memory.
        })
    }

    private evict(entry: OverlayEntry<V>): void {
        try {
            entry.onEvict?.(entry.value)
        } catch {
            // Swallow cleanup errors — they shouldn't break a read.
        }
    }

    whenHydrated(): Promise<void> {
        return this.hydratedPromise
    }

    liveEntries(): readonly OverlayEntrySnapshot<K, V>[] {
        const now = this.now()
        const live: OverlayEntrySnapshot<K, V>[] = []
        const expired: OverlayEntry<V>[] = []
        for (const [key, entry] of this.entries) {
            if (entry.expiresAt <= now) {
                this.entries.delete(key)
                expired.push(entry)
            } else {
                live.push({ key, value: entry.value, expiresAt: entry.expiresAt })
            }
        }
        if (expired.length > 0) {
            for (const entry of expired) this.evict(entry)
            this.persist()
        }
        return live
    }

    read(key: K): V | undefined {
        const entry = this.entries.get(key)
        if (!entry) return undefined
        if (entry.expiresAt <= this.now()) {
            this.entries.delete(key)
            this.evict(entry)
            this.persist()
            return undefined
        }
        return entry.value
    }

    write(key: K, value: V, options?: OverlayWriteOptions<V>): void {
        const opts: OverlayWriteOptions<V> = options ?? {}
        const previous = this.entries.get(key)
        // Build the entry without explicitly setting onEvict to undefined
        // — exactOptionalPropertyTypes rejects { onEvict: undefined } when
        // the target field is `onEvict?: ...` (no `| undefined`).
        const entry: OverlayEntry<V> = opts.onEvict
            ? {
                  value,
                  expiresAt: this.now() + (opts.ttlMs ?? DEFAULT_TTL_MS),
                  onEvict: opts.onEvict,
              }
            : {
                  value,
                  expiresAt: this.now() + (opts.ttlMs ?? DEFAULT_TTL_MS),
              }
        this.entries.set(key, entry)
        if (previous) this.evict(previous)
        this.persist()
    }

    clear(key: K): void {
        const previous = this.entries.get(key)
        if (!previous) return
        this.entries.delete(key)
        this.evict(previous)
        this.persist()
    }

    clearAll(): void {
        const all = [...this.entries.values()]
        this.entries.clear()
        for (const entry of all) this.evict(entry)
        this.persist()
    }
}

export interface CreateLocalOverlayOptions<V> {
    /** Clock injection for tests. Defaults to Date.now. */
    readonly now?: () => number
    /**
     * Optional persistence. The overlay snapshots itself to this
     * `PersistentItem` on every write/clear and hydrates on
     * construction. The persisted shape is
     * `Record<string, {value, expiresAt}>` — the value type V must be
     * JSON-serializable.
     */
    readonly persistence?: PersistentItem<PersistedSnapshot<V>>
    /**
     * Optional validator applied during hydration. Each persisted
     * value is passed through this predicate; entries that fail are
     * dropped. Use it to discard data written by an obsolete schema —
     * without a validator, hydration trusts whatever is in storage.
     */
    readonly validateValue?: OverlayValueValidator<V>
}

/**
 * Create a LocalOverlay scoped to a single value type. The default
 * clock is `Date.now`; override it in tests.
 */
export function createLocalOverlay<K, V>(
    options: CreateLocalOverlayOptions<V> = {}
): LocalOverlay<K, V> {
    return new LocalOverlayImpl<K, V>(
        options.now ?? (() => Date.now()),
        options.persistence ?? null,
        options.validateValue ?? null
    )
}

/**
 * Helper for the common single-value case (no key dimension). Returns
 * a tighter API where `read()` and `write()` don't take a key.
 */
export interface SingleValueOverlay<V> {
    read(): V | undefined
    write(value: V, options?: OverlayWriteOptions<V>): void
    clear(): void
    whenHydrated(): Promise<void>
}

export function createSingleValueOverlay<V>(
    options: CreateLocalOverlayOptions<V> = {}
): SingleValueOverlay<V> {
    // The inner overlay's key type is `string` so hydration matches —
    // persistence stringifies keys via String(key), and the only way for
    // `read` to find a hydrated entry is for the same string to be used
    // both at write time and at read time.
    const inner = createLocalOverlay<string, V>(options)
    const KEY = '_'
    return {
        read: () => inner.read(KEY),
        write: (value, opts) => inner.write(KEY, value, opts),
        clear: () => inner.clear(KEY),
        whenHydrated: () => inner.whenHydrated(),
    }
}
